import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const STORAGE_KEY = "bidArenasLanguage";
const SUPPORTED_LANGUAGES = ["en", "fr", "es"];

const translations = {
  en: {
    "language.label": "Language",
    "language.select": "Select language",
    "language.close": "Close language selector",
    "game.freePlay": "free play",
    "game.balance": "Balance",
    "game.pot": "Pot",
    "game.bids": "Bids",
    "game.lowestUniqueBid": "Lowest unique bid",
    "game.connect": "Connect",
    "game.game": "Game",
    "game.wallet": "Wallet",
    "game.disconnect": "Disconnect",
    "game.history": "History",
    "game.leaderboard": "Leaderboard",
    "game.autoBid": "Auto bid",
    "game.shareResult": "Share Result",
    "game.copySuccess": "Result copied to clipboard! 📋",
    "game.copyFailure": "Failed to copy to clipboard",
    "game.reconnecting": "Reconnecting...",
    "game.youWon": "You won the {amount} pot!",
    "game.bidPlaced": "{amount} bid placed",
    "game.lowestWins": "Lowest unique bid wins the pot...",
    "game.noUniqueRollover": "No unique bids, pot rolls over!",
    "game.potWonByBid": "{pot} pot won by {bid} lowest unique bid",
    "history.submitBid": "submit your bid below",
    "history.bidsEnterPot": "bids are entered into a pot",
    "history.lowestWins": "lowest unique bid wins the pot",
    "history.previousBids": "previous games bids are shown here",
    "history.potRolls": "pot rolls over if there are no unique bids",
    "history.potWonBy": "pot won by",
    "history.lowestUniqueBid": "lowest unique bid",
    "history.noUniqueBids": "No unique bids,",
    "history.potRollsOver": "pot rolls over",
    "leaderboard.loading": "Loading leaderboard...",
    "leaderboard.empty": "No leaderboard data yet.",
    "wallet.connectedBalance": "Connected wallet balance",
    "wallet.depositWithdrawal": "Deposit / Withdrawal",
    "wallet.promotionalBalance": "Promotion wallet balance",
    "wallet.bidArenasBalance": "Bid Arenas balance",
    "wallet.all": "ALL",
    "wallet.readMe": "Read me",
    "wallet.claimCredit": "Claim $5",
    "wallet.claiming": "Claiming...",
    "wallet.claimed": "$5 claimed",
    "wallet.claimFailed": "Unable to claim promotional credit",
    "wallet.promotionConnectMessage": "Connect your crypto wallet to claim $5 promotion credit, which will be added to your game balance. You can withdraw any winnings and any new money added, but the promotion credit cannot be withdrawn.",
    "wallet.deposit": "Deposit",
    "wallet.withdraw": "Withdraw",
    "wallet.depositNoun": "deposit",
    "wallet.withdrawalNoun": "withdrawal",
    "wallet.requested": "Requested: {date}",
    "wallet.completed": "Completed: {date}",
    "wallet.confirmations": "Block confirmations: {current}/{required}",
    "wallet.pending": "Pending...",
    "wallet.viewPolygon": "View on Polygon",
    "wallet.statusNavigation": "Status message navigation",
    "wallet.olderStatus": "Older status message",
    "wallet.newerStatus": "Newer status message",
    "wallet.loading": "Loading...",
    "wallet.connectFirst": "Please connect your wallet first",
    "wallet.providerUnavailable": "Wallet provider not available",
    "wallet.depositUnavailable": "Deposit address unavailable. Please try again.",
    "wallet.validAmountPositive": "Please enter a valid amount greater than 0",
    "wallet.insufficientWallet": "Insufficient balance in wallet",
    "wallet.depositMismatch": "Deposit sent, but it does not match the expected platform address. Please contact support.",
    "wallet.cancelled": "Transaction cancelled by user",
    "wallet.parseError": "Transaction was sent but there was an error parsing the response. Please check your transaction history in a moment to confirm if it succeeded.",
    "wallet.depositFailed": "Deposit failed: {error}",
    "wallet.validAmount": "Please enter a valid amount",
    "wallet.insufficientPlatform": "Insufficient withdrawable balance",
    "wallet.withdrawalFailed": "Withdrawal failed",
    "readMe.notice": "Notice",
    "readMe.restricted": "Real play Bid Arenas is not currently available in your country",
    "readMe.freePlayPots": "In free play mode, pot totals may not correspond to bids placed because free play bids are not included in real play pots. Therefore, real play pots may roll over even though a winner is shown in free play.",
    "readMe.promotionalCredit": "Each wallet may claim one $5 promotional credit. Bids use promotional credit before deposited funds. Wins from promotion-only bids refill a remaining promotional balance to $5 first. If it reaches zero or a bid also uses deposited funds, the full win is withdrawable.",
    "readMe.deposits": "Deposits",
    "readMe.depositIntro": "To fund your Bid Arenas account, send USDC on the Polygon network (Chain ID: 137).",
    "readMe.tokenAddress": "USDC token address",
    "readMe.platformAddress": "Platform wallet address",
    "readMe.usdcOnly": "Send only USDC on Polygon. Sending other tokens or using the wrong network can result in permanent loss of funds.",
    "readMe.depositTiming": "Deposits usually update after blockchain confirmation, typically around 2 minutes.",
    "readMe.withdrawals": "Withdrawals",
    "readMe.withdrawalLimits": "Withdrawals are limited to once per day, up to $10 per withdrawal, and typically complete in 1-2 minutes.",
    "readMe.gasFees": "The platform covers gas fees for withdrawals, so you receive the full withdrawal amount.",
    "readMe.security": "Security",
    "readMe.privateKeys": "Your private keys never leave your wallet. Every transaction requires your explicit approval in MetaMask or WalletConnect.",
    "readMe.permissions": "Connecting your wallet grants permission to view your address and request transactions only. You approve each transaction individually.",
    "readMe.signatures": "Message signatures are used for secure authentication and do not move funds.",
    "readMe.bestPractice": "Best practice: start with a small amount (less than $10) to verify everything works before larger transfers.",
    "modal.wallet": "Wallet",
    "modal.connectWallet": "Connect Wallet",
    "modal.address": "Address",
    "modal.platformBalance": "Platform Balance",
    "modal.walletBalance": "Wallet Balance",
    "modal.disconnectWallet": "Disconnect Wallet",
    "modal.selectProvider": "Select Wallet Provider",
    "modal.detected": "Detected",
    "modal.walletCount": "300+ wallets",
    "modal.browserWallet": "Browser Wallet",
    "modal.noInjectedMobile": "No injected wallet found in this browser. Tap MetaMask to open in the MetaMask app, or use WalletConnect.",
    "modal.noWalletDesktop": "No Web3 wallet detected. Install MetaMask, Coinbase Wallet, or another browser wallet extension.",
    "modal.openMetaMaskPrompt": "If MetaMask doesn't appear, open this page in MetaMask:",
    "modal.openMetaMask": "Open in MetaMask",
    "app.connectionFailed": "Failed to connect wallet: {error}",
    "app.signatureRejected": "You rejected the signature request",
    "app.connectionFailedGeneric": "Failed to connect wallet. Please check the console for details.",
    "app.signatureMessage": "Sign this message to authenticate with Bid Arena.\n\nWallet: {address}\nNonce: {nonce}\n\nThis request will not trigger a blockchain transaction or cost any gas fees.",
    "transactions.deposit": "Deposit",
    "transactions.withdrawal": "Withdrawal",
    "transactions.bid": "Bid",
    "transactions.win": "Win",
    "transactions.history": "Transaction History",
    "transactions.connect": "Please connect your wallet to view transactions",
    "transactions.loading": "Loading transactions...",
    "transactions.pendingDeposits": "Pending Deposits",
    "transactions.empty": "No transactions yet",
    "transactions.confirmations": "confirmations",
    "transactions.explorer": "Explorer",
    "how.title": "How it works",
    "how.step1": "Place a non-refundable bid for as little as $0.10 in Real Play and Free Play.",
    "how.step2": "All bids go into the game prize pool. At the end of the game, the player with the lowest unique bid wins the entire pot.",
    "how.step3": "If multiple players place the same bid amount, those bids are not unique and cannot win.",
    "how.step4": "Connect your wallet and deposit USDC (start with less than $1) to play for real money. You can withdraw at any time.",
  },
  fr: {
    "language.label": "Langue",
    "language.select": "Choisir la langue",
    "language.close": "Fermer le sélecteur de langue",
    "game.freePlay": "jeu gratuit",
    "game.balance": "Solde",
    "game.pot": "Cagnotte",
    "game.bids": "Enchères",
    "game.lowestUniqueBid": "Enchère unique la plus basse",
    "game.connect": "Connect",
    "game.game": "Jeu",
    "game.wallet": "Wallet",
    "game.disconnect": "Déconnecter",
    "game.history": "Historique",
    "game.leaderboard": "Classement",
    "game.autoBid": "Enchère auto",
    "game.shareResult": "Partager le résultat",
    "game.copySuccess": "Résultat copié dans le presse-papiers ! 📋",
    "game.copyFailure": "Impossible de copier le résultat",
    "game.reconnecting": "Reconnexion...",
    "game.youWon": "Vous avez gagné la cagnotte de {amount} !",
    "game.bidPlaced": "Enchère de {amount} placée",
    "game.lowestWins": "L'enchère unique la plus basse remporte la cagnotte...",
    "game.noUniqueRollover": "Aucune enchère unique, la cagnotte est reportée !",
    "game.potWonByBid":
      "Cagnotte de {pot} remportée avec l'enchère unique la plus basse de {bid}",
    "history.submitBid": "placez votre enchère ci-dessous",
    "history.bidsEnterPot": "les enchères alimentent une cagnotte",
    "history.lowestWins": "l'enchère unique la plus basse gagne",
    "history.previousBids": "les enchères précédentes sont affichées ici",
    "history.potRolls": "la cagnotte est reportée sans enchère unique",
    "history.potWonBy": "cagnotte remportée par",
    "history.lowestUniqueBid": "enchère unique la plus basse",
    "history.noUniqueBids": "Aucune enchère unique,",
    "history.potRollsOver": "la cagnotte est reportée",
    "leaderboard.loading": "Chargement du classement...",
    "leaderboard.empty": "Aucune donnée de classement.",
    "wallet.connectedBalance": "Solde du portefeuille connecté",
    "wallet.depositWithdrawal": "Dépôt / Retrait",
    "wallet.promotionalBalance": "Solde promotionnel",
    "wallet.bidArenasBalance": "Solde Bid Arenas",
    "wallet.all": "TOUT",
    "wallet.readMe": "À lire",
    "wallet.claimCredit": "Bonus 5 $",
    "wallet.claiming": "Bonus...",
    "wallet.claimed": "Bonus 5 $ reçu",
    "wallet.claimFailed": "Bonus promotionnel indisponible",
    "wallet.promotionConnectMessage": "Connectez votre portefeuille crypto pour recevoir un bonus promotionnel de 5 $, qui sera ajouté à votre solde de jeu. Vous pouvez retirer vos gains et tout nouvel argent ajouté, mais le bonus promotionnel ne peut pas être retiré.",
    "wallet.deposit": "Déposer",
    "wallet.withdraw": "Retirer",
    "wallet.depositNoun": "dépôt",
    "wallet.withdrawalNoun": "retrait",
    "wallet.requested": "Demandé : {date}",
    "wallet.completed": "Terminé : {date}",
    "wallet.confirmations": "Confirmations de bloc : {current}/{required}",
    "wallet.pending": "En attente...",
    "wallet.viewPolygon": "Voir sur Polygon",
    "wallet.statusNavigation": "Navigation des statuts",
    "wallet.olderStatus": "Statut précédent",
    "wallet.newerStatus": "Statut suivant",
    "wallet.loading": "Chargement...",
    "wallet.connectFirst": "Connectez d'abord votre portefeuille",
    "wallet.providerUnavailable": "Fournisseur de portefeuille indisponible",
    "wallet.depositUnavailable": "Adresse de dépôt indisponible. Réessayez.",
    "wallet.validAmountPositive": "Saisissez un montant valide supérieur à 0",
    "wallet.insufficientWallet": "Solde insuffisant dans le portefeuille",
    "wallet.depositMismatch": "Le dépôt a été envoyé, mais ne correspond pas à l'adresse attendue. Contactez l'assistance.",
    "wallet.cancelled": "Transaction annulée par l'utilisateur",
    "wallet.parseError": "La transaction a été envoyée, mais la réponse n'a pas pu être lue. Vérifiez bientôt l'historique des transactions.",
    "wallet.depositFailed": "Échec du dépôt : {error}",
    "wallet.validAmount": "Saisissez un montant valide",
    "wallet.insufficientPlatform": "Solde retirable insuffisant",
    "wallet.withdrawalFailed": "Échec du retrait",
    "readMe.notice": "Avis",
    "readMe.restricted": "Le mode réel de Bid Arenas n'est actuellement pas disponible dans votre pays",
    "readMe.freePlayPots": "En mode gratuit, le montant des cagnottes peut ne pas correspondre aux enchères placées, car les enchères gratuites ne sont pas incluses dans les cagnottes du mode réel. Une cagnotte réelle peut donc être reportée même si un gagnant est affiché en mode gratuit.",
    "readMe.promotionalCredit": "Chaque portefeuille peut recevoir une seule fois un bonus promotionnel de 5 $. Les enchères utilisent d'abord le bonus. Les gains issus d'enchères financées uniquement par le bonus rétablissent d'abord un bonus restant à 5 $. Si le bonus atteint zéro ou si l'enchère utilise aussi des fonds déposés, le gain entier est retirable.",
    "readMe.deposits": "Dépôts",
    "readMe.depositIntro": "Pour approvisionner votre compte Bid Arenas, envoyez des USDC sur le réseau Polygon (ID de chaîne : 137).",
    "readMe.tokenAddress": "Adresse du jeton USDC",
    "readMe.platformAddress": "Adresse du portefeuille de la plateforme",
    "readMe.usdcOnly": "Envoyez uniquement des USDC sur Polygon. Tout autre jeton ou réseau peut entraîner une perte définitive des fonds.",
    "readMe.depositTiming": "Les dépôts sont généralement mis à jour après confirmation de la blockchain, en environ 2 minutes.",
    "readMe.withdrawals": "Retraits",
    "readMe.withdrawalLimits": "Les retraits sont limités à un par jour, jusqu'à 10 $ par retrait, et prennent généralement 1 à 2 minutes.",
    "readMe.gasFees": "La plateforme paie les frais de gas ; vous recevez donc le montant total du retrait.",
    "readMe.security": "Sécurité",
    "readMe.privateKeys": "Vos clés privées ne quittent jamais votre portefeuille. Chaque transaction nécessite votre approbation explicite dans MetaMask ou WalletConnect.",
    "readMe.permissions": "La connexion autorise uniquement la consultation de votre adresse et les demandes de transaction. Vous approuvez chaque transaction.",
    "readMe.signatures": "Les signatures de message servent à l'authentification sécurisée et ne déplacent aucun fonds.",
    "readMe.bestPractice": "Conseil : commencez avec un petit montant (moins de 10 $) avant d'effectuer des transferts plus importants.",
    "modal.wallet": "Portefeuille",
    "modal.connectWallet": "Connecter le portefeuille",
    "modal.address": "Adresse",
    "modal.platformBalance": "Solde de la plateforme",
    "modal.walletBalance": "Solde du portefeuille",
    "modal.disconnectWallet": "Déconnecter le portefeuille",
    "modal.selectProvider": "Choisir un fournisseur de portefeuille",
    "modal.detected": "Détecté",
    "modal.walletCount": "Plus de 300 portefeuilles",
    "modal.browserWallet": "Portefeuille du navigateur",
    "modal.noInjectedMobile": "Aucun portefeuille injecté trouvé. Touchez MetaMask pour ouvrir l'application, ou utilisez WalletConnect.",
    "modal.noWalletDesktop": "Aucun portefeuille Web3 détecté. Installez MetaMask, Coinbase Wallet ou une autre extension.",
    "modal.openMetaMaskPrompt": "Si MetaMask n'apparaît pas, ouvrez cette page dans MetaMask :",
    "modal.openMetaMask": "Ouvrir dans MetaMask",
    "app.connectionFailed": "Échec de connexion au portefeuille : {error}",
    "app.signatureRejected": "Vous avez refusé la demande de signature",
    "app.connectionFailedGeneric": "Échec de connexion au portefeuille. Consultez la console pour plus de détails.",
    "app.signatureMessage": "Signez ce message pour vous authentifier auprès de Bid Arena.\n\nPortefeuille : {address}\nNonce : {nonce}\n\nCette demande ne déclenchera aucune transaction blockchain et ne coûtera aucuns frais de gas.",
    "transactions.deposit": "Dépôt",
    "transactions.withdrawal": "Retrait",
    "transactions.bid": "Enchère",
    "transactions.win": "Gain",
    "transactions.history": "Historique des transactions",
    "transactions.connect": "Connectez votre portefeuille pour voir les transactions",
    "transactions.loading": "Chargement des transactions...",
    "transactions.pendingDeposits": "Dépôts en attente",
    "transactions.empty": "Aucune transaction",
    "transactions.confirmations": "confirmations",
    "transactions.explorer": "Explorateur",
    "how.title": "Fonctionnement",
    "how.step1": "Placez une enchère non remboursable à partir de 0,10 $ en mode réel ou gratuit.",
    "how.step2": "Toutes les enchères alimentent la cagnotte. À la fin, l'enchère unique la plus basse remporte toute la cagnotte.",
    "how.step3": "Si plusieurs joueurs placent le même montant, ces enchères ne sont pas uniques et ne peuvent pas gagner.",
    "how.step4": "Connectez votre portefeuille et déposez des USDC (commencez avec moins de 1 $) pour jouer en argent réel. Vous pouvez retirer à tout moment.",
  },
  es: {
    "language.label": "Idioma",
    "language.select": "Seleccionar idioma",
    "language.close": "Cerrar el selector de idioma",
    "game.freePlay": "juego gratis",
    "game.balance": "Saldo",
    "game.pot": "Bote",
    "game.bids": "Pujas",
    "game.lowestUniqueBid": "Puja única más baja",
    "game.connect": "Conectar",
    "game.game": "Juego",
    "game.wallet": "Cartera",
    "game.disconnect": "Desconectar",
    "game.history": "Historial",
    "game.leaderboard": "Clasificación",
    "game.autoBid": "Puja auto",
    "game.shareResult": "Compartir resultado",
    "game.copySuccess": "¡Resultado copiado! 📋",
    "game.copyFailure": "No se pudo copiar el resultado",
    "game.reconnecting": "Reconectando...",
    "game.youWon": "¡Has ganado el bote de {amount}!",
    "game.bidPlaced": "Puja de {amount} realizada",
    "game.lowestWins": "La puja única más baja gana el bote...",
    "game.noUniqueRollover": "No hay pujas únicas; el bote se acumula.",
    "game.potWonByBid":
      "Bote de {pot} ganado con la puja única más baja de {bid}",
    "history.submitBid": "realiza tu puja abajo",
    "history.bidsEnterPot": "las pujas se añaden a un bote",
    "history.lowestWins": "la puja única más baja gana el bote",
    "history.previousBids": "aquí aparecen las pujas anteriores",
    "history.potRolls": "el bote se acumula si no hay puja única",
    "history.potWonBy": "bote ganado por",
    "history.lowestUniqueBid": "puja única más baja",
    "history.noUniqueBids": "No hay pujas únicas,",
    "history.potRollsOver": "el bote se acumula",
    "leaderboard.loading": "Cargando clasificación...",
    "leaderboard.empty": "Aún no hay datos de clasificación.",
    "wallet.connectedBalance": "Saldo de la cartera conectada",
    "wallet.depositWithdrawal": "Depósito / Retiro",
    "wallet.promotionalBalance": "Saldo promocional",
    "wallet.bidArenasBalance": "Saldo de Bid Arenas",
    "wallet.all": "TODO",
    "wallet.readMe": "Leer",
    "wallet.claimCredit": "Bonus 5 $",
    "wallet.claiming": "Bonus...",
    "wallet.claimed": "Bonus 5 $ recibido",
    "wallet.claimFailed": "Bonus promocional no disponible",
    "wallet.promotionConnectMessage": "Conecta tu cartera de criptomonedas para recibir un bonus promocional de 5 $, que se añadirá a tu saldo de juego. Puedes retirar las ganancias y cualquier dinero nuevo que añadas, pero el bonus promocional no se puede retirar.",
    "wallet.deposit": "Depositar",
    "wallet.withdraw": "Retirar",
    "wallet.depositNoun": "depósito",
    "wallet.withdrawalNoun": "retiro",
    "wallet.requested": "Solicitado: {date}",
    "wallet.completed": "Completado: {date}",
    "wallet.confirmations": "Confirmaciones de bloque: {current}/{required}",
    "wallet.pending": "Pendiente...",
    "wallet.viewPolygon": "Ver en Polygon",
    "wallet.statusNavigation": "Navegación de estados",
    "wallet.olderStatus": "Estado anterior",
    "wallet.newerStatus": "Estado siguiente",
    "wallet.loading": "Cargando...",
    "wallet.connectFirst": "Conecta primero tu cartera",
    "wallet.providerUnavailable": "Proveedor de cartera no disponible",
    "wallet.depositUnavailable": "Dirección de depósito no disponible. Inténtalo de nuevo.",
    "wallet.validAmountPositive": "Introduce un importe válido superior a 0",
    "wallet.insufficientWallet": "Saldo insuficiente en la cartera",
    "wallet.depositMismatch": "El depósito se envió, pero no coincide con la dirección esperada. Contacta con soporte.",
    "wallet.cancelled": "Transacción cancelada por el usuario",
    "wallet.parseError": "La transacción se envió, pero hubo un error al leer la respuesta. Revisa el historial en unos instantes.",
    "wallet.depositFailed": "Error en el depósito: {error}",
    "wallet.validAmount": "Introduce un importe válido",
    "wallet.insufficientPlatform": "Saldo retirable insuficiente",
    "wallet.withdrawalFailed": "Error en el retiro",
    "readMe.notice": "Aviso",
    "readMe.restricted": "El juego con dinero real de Bid Arenas no está disponible actualmente en tu país",
    "readMe.freePlayPots": "En el modo gratuito, los totales de los botes pueden no corresponder con las pujas realizadas porque las pujas gratuitas no se incluyen en los botes del juego con dinero real. Por lo tanto, los botes del juego real pueden acumularse aunque se muestre un ganador en el modo gratuito.",
    "readMe.promotionalCredit": "Cada cartera puede recibir una sola vez un bonus promocional de 5 $. Las pujas usan primero el bonus. Las ganancias de pujas financiadas solo con el bonus reponen primero un bonus restante hasta 5 $. Si llega a cero o la puja también usa fondos depositados, la ganancia completa se puede retirar.",
    "readMe.deposits": "Depósitos",
    "readMe.depositIntro": "Para ingresar fondos en Bid Arenas, envía USDC por la red Polygon (ID de cadena: 137).",
    "readMe.tokenAddress": "Dirección del token USDC",
    "readMe.platformAddress": "Dirección de la cartera de la plataforma",
    "readMe.usdcOnly": "Envía únicamente USDC por Polygon. Otros tokens o redes pueden provocar la pérdida permanente de los fondos.",
    "readMe.depositTiming": "Los depósitos suelen actualizarse tras la confirmación de la cadena de bloques, normalmente en unos 2 minutos.",
    "readMe.withdrawals": "Retiros",
    "readMe.withdrawalLimits": "Los retiros están limitados a uno al día, hasta 10 $ por retiro, y suelen completarse en 1-2 minutos.",
    "readMe.gasFees": "La plataforma cubre las comisiones de gas, por lo que recibes el importe total.",
    "readMe.security": "Seguridad",
    "readMe.privateKeys": "Tus claves privadas nunca salen de tu cartera. Cada transacción requiere tu aprobación explícita en MetaMask o WalletConnect.",
    "readMe.permissions": "Conectar la cartera solo permite ver tu dirección y solicitar transacciones. Tú apruebas cada transacción.",
    "readMe.signatures": "Las firmas de mensajes se usan para la autenticación segura y no transfieren fondos.",
    "readMe.bestPractice": "Recomendación: empieza con un importe pequeño (menos de 10 $) antes de realizar transferencias mayores.",
    "modal.wallet": "Cartera",
    "modal.connectWallet": "Conectar cartera",
    "modal.address": "Dirección",
    "modal.platformBalance": "Saldo de la plataforma",
    "modal.walletBalance": "Saldo de la cartera",
    "modal.disconnectWallet": "Desconectar cartera",
    "modal.selectProvider": "Selecciona un proveedor de cartera",
    "modal.detected": "Detectada",
    "modal.walletCount": "Más de 300 carteras",
    "modal.browserWallet": "Cartera del navegador",
    "modal.noInjectedMobile": "No se encontró una cartera integrada. Toca MetaMask para abrir la aplicación o usa WalletConnect.",
    "modal.noWalletDesktop": "No se detectó una cartera Web3. Instala MetaMask, Coinbase Wallet u otra extensión.",
    "modal.openMetaMaskPrompt": "Si MetaMask no aparece, abre esta página en MetaMask:",
    "modal.openMetaMask": "Abrir en MetaMask",
    "app.connectionFailed": "Error al conectar la cartera: {error}",
    "app.signatureRejected": "Has rechazado la solicitud de firma",
    "app.connectionFailedGeneric": "No se pudo conectar la cartera. Consulta la consola para más detalles.",
    "app.signatureMessage": "Firma este mensaje para autenticarte en Bid Arena.\n\nCartera: {address}\nNonce: {nonce}\n\nEsta solicitud no iniciará ninguna transacción en la cadena de bloques ni tendrá comisiones de gas.",
    "transactions.deposit": "Depósito",
    "transactions.withdrawal": "Retiro",
    "transactions.bid": "Puja",
    "transactions.win": "Ganancia",
    "transactions.history": "Historial de transacciones",
    "transactions.connect": "Conecta tu cartera para ver las transacciones",
    "transactions.loading": "Cargando transacciones...",
    "transactions.pendingDeposits": "Depósitos pendientes",
    "transactions.empty": "Aún no hay transacciones",
    "transactions.confirmations": "confirmaciones",
    "transactions.explorer": "Explorador",
    "how.title": "Cómo funciona",
    "how.step1": "Realiza una puja no reembolsable desde 0,10 $ en juego real o gratuito.",
    "how.step2": "Todas las pujas se añaden al bote. Al terminar, la puja única más baja gana todo el bote.",
    "how.step3": "Si varios jugadores realizan la misma puja, esas pujas no son únicas y no pueden ganar.",
    "how.step4": "Conecta tu cartera y deposita USDC (empieza con menos de 1 $) para jugar con dinero real. Puedes retirar en cualquier momento.",
  },
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    const savedLanguage = localStorage.getItem(STORAGE_KEY);
    return SUPPORTED_LANGUAGES.includes(savedLanguage) ? savedLanguage : "en";
  });

  const setLanguage = useCallback((nextLanguage) => {
    const normalizedLanguage = SUPPORTED_LANGUAGES.includes(nextLanguage)
      ? nextLanguage
      : "en";
    localStorage.setItem(STORAGE_KEY, normalizedLanguage);
    setLanguageState(normalizedLanguage);
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const t = useCallback(
    (key, values = {}) => {
      const template =
        translations[language]?.[key] ?? translations.en[key] ?? key;
      return Object.entries(values).reduce(
        (result, [name, value]) =>
          result.replaceAll(`{${name}}`, String(value)),
        template,
      );
    },
    [language],
  );

  const value = useMemo(
    () => ({ language, setLanguage, t }),
    [language, setLanguage, t],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useTranslation = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useTranslation must be used within LanguageProvider");
  }
  return context;
};
